import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DocsService, IDbTemplateItem } from '@shared/services/docs.service';
import { NotificationService } from '@shared/services/notification.service';
import { IPrinter, PrintService } from '@shared/services/print.service';
import { catchError, of, Subject, takeUntil } from 'rxjs';

type PrintTestMode = 'url' | 'template';

@Component({
	selector: 'app-print-test',
	templateUrl: './print-test.component.html',
	styleUrl: './print-test.component.scss',
	standalone: false,
})
export class PrintTestComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	printers: IPrinter[] = [];
	templates: IDbTemplateItem[] = [];
	isSubmitting = false;

	form: FormGroup = this.fb.group({
		printer: ['', Validators.required],
		mode: ['url' as PrintTestMode, Validators.required],
		urls: [''],
		template: [''],
		data: [''],
	});

	constructor(
		private fb: FormBuilder,
		private printService: PrintService,
		private docsService: DocsService,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		// A user reaching this page via print:LabelPost alone (no printer/template read access)
		// should still see the form rather than an unhandled error - these lookups fail soft into
		// an empty list, same pattern as the Collection Jobs / Print Jobs pages.
		this.printService
			.getPrinters()
			.pipe(
				catchError(() => of<IPrinter[]>([])),
				takeUntil(this.destroy$),
			)
			.subscribe((printers) => (this.printers = printers));

		this.docsService
			.getTemplateList()
			.pipe(
				catchError(() => of<{ result: IDbTemplateItem[] }>({ result: [] })),
				takeUntil(this.destroy$),
			)
			.subscribe((response) => (this.templates = response.result));
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get isUrlMode(): boolean {
		return this.form.get('mode')?.value === 'url';
	}

	submit(): void {
		const { printer, mode, urls, template, data } = this.form.value as {
			printer: string;
			mode: PrintTestMode;
			urls: string;
			template: string;
			data: string;
		};

		if (!printer) {
			this.notify('print.test.error.header', 'print.test.error.no-printer');
			return;
		}

		if (mode === 'url') {
			const urlList = (urls ?? '')
				.split('\n')
				.map((url) => url.trim())
				.filter(Boolean);
			if (!urlList.length) {
				this.notify('print.test.error.header', 'print.test.error.no-urls');
				return;
			}

			this.isSubmitting = true;
			this.printService
				.print(printer, urlList)
				.pipe(takeUntil(this.destroy$))
				.subscribe({ next: () => this.onSuccess(), error: () => this.onError() });
			return;
		}

		if (!template) {
			this.notify('print.test.error.header', 'print.test.error.no-template');
			return;
		}

		let parsedData: unknown;
		try {
			parsedData = data ? JSON.parse(data) : {};
		} catch {
			this.notify('print.test.error.header', 'print.test.error.invalid-json');
			return;
		}

		this.isSubmitting = true;
		this.printService
			.printWithDoc(parsedData, template, printer)
			.pipe(takeUntil(this.destroy$))
			.subscribe({ next: () => this.onSuccess(), error: () => this.onError() });
	}

	private onSuccess(): void {
		this.isSubmitting = false;
		this.notify('print.test.success.header', 'print.test.success.content', 'success');
	}

	private onError(): void {
		this.isSubmitting = false;
		this.notify('print.test.error.header', 'print.test.error.content');
	}

	private notify(headerKey: string, contentKey: string, type: 'error' | 'success' = 'error'): void {
		this.notificationService.notify([headerKey], [contentKey], type);
	}
}
